'use client'

import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { Toast, ToastType, ToastVariant } from './Toast'
import '../../styles/toast.css'

export type ToastPosition =
  | 'top-right'
  | 'top-left'
  | 'top-center'
  | 'bottom-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'center'

interface ToastItem {
  id: string
  message: ReactNode
  type: ToastType
  duration?: number
  variant?: ToastVariant
  position?: ToastPosition
  onClick?: () => void
}

export interface ToastOptions {
  variant?: ToastVariant
  position?: ToastPosition
}

interface ToastContextType {
  showToast: (message: ReactNode, type?: ToastType, duration?: number, onClick?: () => void, options?: ToastOptions) => string
  dismissToast: (id: string) => void
  dismissAll: () => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return context
}

interface ToastProviderProps {
  children: ReactNode
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const seqRef = useState(() => ({ n: 0 }))[0]

  const showToast = useCallback((message: ReactNode, type: ToastType = 'info', duration: number = 3000, onClick?: () => void, options?: ToastOptions) => {
    let id = ''
    try {
      id = (globalThis as any).crypto?.randomUUID?.() || ''
    } catch {
      // ignore
    }
    if (!id) {
      seqRef.n += 1
      id = `${Date.now()}-${seqRef.n}-${Math.random().toString(16).slice(2)}`
    }
    setToasts(prev => [...prev, { id, message, type, duration, onClick, variant: options?.variant, position: options?.position || 'top-right' }])
    return id
  }, [seqRef])

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id))
  }, [])

  const dismissAll = useCallback(() => {
    setToasts([])
  }, [])

  return (
    <ToastContext.Provider value={{ showToast, dismissToast, dismissAll }}>
      {children}
      {(['top-right', 'top-left', 'top-center', 'bottom-right', 'bottom-left', 'bottom-center', 'center'] as ToastPosition[]).map((position) => {
        const positionToasts = toasts.filter((toast) => (toast.position || 'top-right') === position)
        if (positionToasts.length === 0) return null

        return (
          <div key={position} className={`toast-container toast-container--${position}`}>
            {positionToasts.map(toast => (
              <Toast
                key={toast.id}
                message={toast.message}
                type={toast.type}
                duration={toast.duration}
                variant={toast.variant}
                onClick={toast.onClick}
                onClose={() => dismissToast(toast.id)}
              />
            ))}
          </div>
        )
      })}
    </ToastContext.Provider>
  )
}
